import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { MedicalActRecord } from '@/core/models/medical-act.model'
import {
    useGetMedicalActRecordsByConsultationQuery,
    useGetMedicalActRecordsByExpertiseQuery,
    useGetMedicalActRecordsByPatientQuery,
    useDeleteMedicalActRecordMutation,
} from '@/core/services/medical-act'

export type UseMedicalActRecordsProps = {
    patientId: string
    consultationId?: string
    expertiseId?: string
}

export function useMedicalActRecords({
    patientId,
    consultationId,
    expertiseId,
}: UseMedicalActRecordsProps) {
    const { t } = useTranslation()
    const [isModalOpen, setIsModalOpen] = useState(false)

    // Query based on context
    const {
        data: consultationRecords,
        isLoading: isLoadingConsultation,
        refetch: refetchConsultation,
    } = useGetMedicalActRecordsByConsultationQuery(consultationId || '', {
        skip: !consultationId,
    })

    const {
        data: expertiseRecords,
        isLoading: isLoadingExpertise,
        refetch: refetchExpertise,
    } = useGetMedicalActRecordsByExpertiseQuery(expertiseId || '', {
        skip: !expertiseId,
    })

    const {
        data: patientRecords,
        isLoading: isLoadingPatient,
        refetch: refetchPatient,
    } = useGetMedicalActRecordsByPatientQuery(patientId, {
        skip: !!(consultationId || expertiseId),
    })

    const [deleteRecord, { isLoading: isDeleting }] = useDeleteMedicalActRecordMutation()

    // Get the appropriate records based on context
    const records: MedicalActRecord[] | undefined = consultationId
        ? consultationRecords
        : expertiseId
            ? expertiseRecords
            : patientRecords

    const isLoading = isLoadingConsultation || isLoadingExpertise || isLoadingPatient

    const handleRefetch = () => {
        if (consultationId) refetchConsultation()
        else if (expertiseId) refetchExpertise()
        else refetchPatient()
    }

    const handleOpenModal = () => setIsModalOpen(true)
    const handleCloseModal = () => setIsModalOpen(false)

    const handleDelete = async (recordId: string) => {
        if (window.confirm(t('medicalAct.record.deleteConfirm', { defaultValue: 'Delete this medical act record?' }))) {
            try {
                await deleteRecord(recordId).unwrap()
                handleRefetch()
            } catch (error) {
                // Error handled by RTK Query
            }
        }
    }

    // Calculate total revenue
    const totalRevenue = records?.reduce((sum, r) => sum + r.priceAtTime, 0) || 0

    return {
        records,
        isLoading,
        isDeleting,
        isModalOpen,
        totalRevenue,
        handleRefetch,
        handleOpenModal,
        handleCloseModal,
        handleDelete,
    }
}

export { formatPrice } from '@/utils/formatPrice'

export function formatDate(date: string) {
    return new Date(date).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}
